import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Popover from '../shared/Popover'
import { getCookieValue, setCookie } from '../../utils/cookies'

const POPOVER_COOKIE_NAME = 'notifications_popover_views'
const MAX_POPOVER_VIEWS = 2

const Sidebar = ({ horizontal = false }) => {
  const [showNotificationsPopover, setShowNotificationsPopover] = useState(false)
  const navigate = useNavigate()
  const [error, setError] = React.useState(null)
  const [scope, setScope] = React.useState(null)

  useEffect(() => {
    const initializePopover = () => {
      const viewCount = parseInt(getCookieValue(POPOVER_COOKIE_NAME))
      const isNotificationsPage = window.location.pathname === '/collectors/notifications'

      if (viewCount < MAX_POPOVER_VIEWS && !isNotificationsPage) {
        const timer = setTimeout(() => {
          setShowNotificationsPopover(true)
        }, 1000)

        return () => clearTimeout(timer)
      }
    }

    fetchAuthInfo().then(() => {
      initializePopover()
    })
  }, [])

  const handlePopoverClose = () => {
    setShowNotificationsPopover(false)

    const viewCount = parseInt(getCookieValue(POPOVER_COOKIE_NAME))
    setCookie(POPOVER_COOKIE_NAME, viewCount + 1)
  }

  const fetchAuthInfo = async () => {
    const response = await fetch('/collectors/api/check_auth', {
      headers: {
        Accept: 'application/json',
      },
    })
    const data = await response.json()
    setScope(data.scope)
  }

  const links = React.useMemo(() => {
    const baseLinks = [
      { name: 'Accounts', path: '/collectors/accounts' },
      { name: 'Payout Configuration', path: '/collectors/direct_deposit' },
    ]

    if (scope === 'admin') {
      baseLinks.push({
        name: 'Users',
        path: '/collectors/users',
      })
    }

    baseLinks.push({
      name: 'Notifications',
      path: '/collectors/notifications',
      popover: {
        title: 'Notifications Center',
        content: (
          <>
            Configure who receives notifications about each settlement.
            <br />
            <Link
              to='/collectors/notifications'
              className='text-primary'
              onClick={handlePopoverClose}
            >
              View Notifications →
            </Link>
          </>
        ),
      },
    })

    return baseLinks
  }, [scope])

  const handleLogout = async () => {
    try {
      const response = await fetch('/collectors/api/logout', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      })

      if (response.ok) {
        navigate('/collectors')
      } else {
        setError('Logout failed. Please try again.')
        setTimeout(() => setError(null), 5000) // Clear error after 5 seconds
      }
    } catch {
      setError('Network error during logout. Please try again.')
      setTimeout(() => setError(null), 5000) // Clear error after 5 seconds
    }
  }

  const isCurrentPage = path => {
    return window.location.pathname === path
  }

  const renderError = () => {
    if (!error) return null

    return (
      <div
        className='alert alert-danger alert-dismissible fade show'
        role='alert'
        style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1050,
          minWidth: '300px',
        }}
      >
        {error}
        <button
          type='button'
          className='btn-close'
          onClick={() => setError(null)}
          aria-label='Close'
        ></button>
      </div>
    )
  }

  if (horizontal) {
    return (
      <>
        {renderError()}
        <div className='d-flex mb-4'>
          <ul className='nav nav-tabs'>
            {links.map(link => (
              <li className='nav-item' key={link.name} style={{ position: 'relative' }}>
                <a
                  href={link.path}
                  className={`nav-link ${isCurrentPage(link.path) ? 'active' : ''}`}
                >
                  {link.name}
                </a>
                {link.popover && showNotificationsPopover && (
                  <Popover
                    title={link.popover.title}
                    content={link.popover.content}
                    isVisible={true}
                    onClose={handlePopoverClose}
                    placement='right'
                  />
                )}
              </li>
            ))}
            <li className='nav-item'>
              <button onClick={handleLogout} className='nav-link text-danger'>
                Logout
              </button>
            </li>
          </ul>
        </div>
      </>
    )
  }

  return (
    <div className='col-md-auto mb-10'>
      {renderError()}
      <div className='card rounded-top-start-3 rounded-bottom-end-3'>
        <div className='list-group list-group-flush pt-8 pb-12'>
          {links.map(link => (
            <div key={link.name} style={{ position: 'relative' }}>
              <Link
                to={link.path}
                className={`list-group-item list-group-item-action border-0 px-4 py-2 ${
                  isCurrentPage(link.path) ? 'text-primary' : ''
                }`}
              >
                {link.name}
              </Link>
              {link.popover && showNotificationsPopover && (
                <Popover
                  title={link.popover.title}
                  content={link.popover.content}
                  isVisible={true}
                  onClose={handlePopoverClose}
                  placement='right'
                />
              )}
            </div>
          ))}
          <button
            onClick={handleLogout}
            className='list-group-item list-group-item-action border-0 px-4 py-2'
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  )
}

export default Sidebar
